import {
  VIRTUAL_WIDTH,
  VIRTUAL_HEIGHT,
  PLAYFIELD,
  GAME_STATES,
  DIFFICULTIES,
  COLLECTIBLE_TYPES,
  BRICK_TYPES,
} from './Constants';
import { Paddle } from '../entities/Paddle';
import { Ball } from '../entities/Ball';
import { Laser } from '../entities/Laser';
import { ParticleSystem } from '../entities/ParticleSystem';
import { LevelManager } from '../levels/LevelManager';
import { AudioManager } from '../audio/AudioManager';
import { InputManager } from './InputManager';
import { WebGLRenderer } from '../rendering/WebGLRenderer';
import { OverlayUI } from '../ui/OverlayUI';

/**
 * Main Game Engine with 4x Physics Sub-stepping and Anti-Stall Collision Resolution
 */
export class Engine {
  constructor(canvas, uiContainer) {
    this.canvas = canvas;
    this.canvas.width = VIRTUAL_WIDTH;
    this.canvas.height = VIRTUAL_HEIGHT;

    // Subsystems
    this.audio = new AudioManager();
    this.input = new InputManager(this.canvas);
    this.renderer = new WebGLRenderer(this.canvas);
    this.levelManager = new LevelManager();
    this.ui = new OverlayUI(uiContainer, this);

    // Gameplay Settings
    this.difficulty = DIFFICULTIES.NORMAL;
    this.state = GAME_STATES.MENU;
    this.previousState = GAME_STATES.MENU;

    // Game Entities
    this.paddle = new Paddle(this.difficulty);
    this.balls = [];
    this.capsules = [];
    this.lasers = [];
    this.particleSystem = new ParticleSystem();
    this.bricks = [];

    // Stats
    this.score = 0;
    this.highScore = parseInt(localStorage.getItem('arkanoid_c64_high') || '50000', 10);
    this.lives = this.difficulty.initialLives;
    this.gameTime = 0.0;

    // Level Clear Delay Timer
    this.levelClearTimer = 0;

    // Fixed timestep accumulator
    this.fixedDt = 1.0 / 60.0;
    this.lastTime = 0;
    this.accumulator = 0;

    this.ui.showState(this.state);
    this.ui.updateHUD(this.score, this.highScore, 1, this.lives);
  }

  setDifficulty(diff) {
    this.difficulty = diff;
    this.lives = diff.initialLives;
    this.paddle.reset(diff);
    this.ui.updateHUD(this.score, this.highScore, this.levelManager.getCurrentLevelNumber(), this.lives);
  }

  showMenu() {
    this.input.exitPointerLock();
    this.state = GAME_STATES.MENU;
    this.audio.playMenuBGM();
    this.ui.showState(this.state);
  }

  openSettings() {
    this.previousState = this.state;
    this.state = GAME_STATES.SETTINGS;
    this.input.exitPointerLock();
    this.ui.showState(this.state);
  }

  closeSettings() {
    this.state = this.previousState || GAME_STATES.MENU;
    if (this.state === GAME_STATES.PLAYING) {
      this.input.requestPointerLock();
    }
    this.ui.showState(this.state);
  }

  startGame() {
    this.audio.unlockAudio();
    this.score = 0;
    this.lives = this.difficulty.initialLives;
    this.levelManager.loadLevel(0, this.difficulty);
    this.bricks = this.levelManager.bricks;

    this.resetFieldForLife();
    this.state = GAME_STATES.PLAYING;

    // Start Level 1 Background Music
    this.audio.playLevelBGM(1);

    this.input.requestPointerLock();
    this.ui.showState(this.state);
    this.ui.updateHUD(this.score, this.highScore, this.levelManager.getCurrentLevelNumber(), this.lives);
  }

  /**
   * Start the game at a specific level (used by the cheat/warp system)
   */
  startGameAtLevel(levelIndex) {
    this.audio.unlockAudio();
    this.score = 0;
    this.lives = this.difficulty.initialLives;
    this.levelManager.loadLevel(levelIndex, this.difficulty);
    this.bricks = this.levelManager.bricks;

    this.resetFieldForLife();
    this.state = GAME_STATES.PLAYING;

    const levelNum = this.levelManager.getCurrentLevelNumber();
    this.audio.playLevelBGM(levelNum);

    this.input.requestPointerLock();
    this.ui.showState(this.state);
    this.ui.updateHUD(this.score, this.highScore, levelNum, this.lives);
  }

  resetFieldForLife() {
    this.paddle.reset(this.difficulty);
    this.capsules = [];
    this.lasers = [];
    this.particleSystem.clear();

    const ball = new Ball(0, 0, 5, this.difficulty.ballSpeed);
    ball.attachToPaddle(this.paddle, 0);
    this.balls = [ball];
  }

  togglePause() {
    if (this.state === GAME_STATES.PLAYING) {
      this.state = GAME_STATES.PAUSED;
      this.input.exitPointerLock();
      this.audio.playMenuSelect();
      this.ui.showState(this.state);
    } else if (this.state === GAME_STATES.PAUSED) {
      this.state = GAME_STATES.PLAYING;
      this.input.requestPointerLock();
      this.audio.playMenuSelect();
      this.ui.showState(this.state);
    }
  }

  start() {
    this.lastTime = performance.now();
    this.audio.playMenuBGM();
    requestAnimationFrame(this.loop.bind(this));
  }

  loop(currentTime) {
    const elapsed = Math.min((currentTime - this.lastTime) / 1000.0, 0.1);
    this.lastTime = currentTime;
    this.accumulator += elapsed;

    this.handleInput();

    // Fixed timestep updates
    while (this.accumulator >= this.fixedDt) {
      this.update(this.fixedDt);
      this.accumulator -= this.fixedDt;
    }

    // Render using 100% WebGL Fragment Shader
    this.render(elapsed);

    this.input.clearJustPressed();
    requestAnimationFrame(this.loop.bind(this));
  }

  handleInput() {
    if (this.input.wasKeyJustPressed('KeyP')) {
      this.togglePause();
    }
    if (this.input.wasKeyJustPressed('KeyM')) {
      const muted = this.audio.toggleMute();
      this.ui.updateSettingsDisplay();
    }
    if (this.input.wasKeyJustPressed('KeyC')) {
      this.renderer.toggleCRT();
      this.ui.updateSettingsDisplay();
    }
  }

  update(dt) {
    if (this.state !== GAME_STATES.PLAYING) {
      if (this.state === GAME_STATES.LEVEL_CLEAR) {
        this.levelClearTimer -= dt;
        if (this.levelClearTimer <= 0) {
          this.advanceToNextLevel();
        }
      }
      return;
    }

    this.gameTime += dt;

    // 1. Update Paddle with Pointer Lock or Direct Mouse/Keyboard
    this.paddle.update(dt, this.input);

    // 2. Launch Balls or Fire Lasers on Space / Click
    const launchRequested = this.input.wasKeyJustPressed('Space') || (this.input.isMouseDown);
    if (launchRequested) {
      this.balls.forEach((ball) => {
        if (ball.isStuck) {
          ball.launch();
          this.audio.playPaddleBounce();
        }
      });

      if (this.paddle.canFireLaser(performance.now())) {
        this.paddle.recordLaserFired(performance.now());
        const leftLaser = new Laser(this.paddle.x + 8, this.paddle.y - 4);
        const rightLaser = new Laser(this.paddle.x + this.paddle.width - 8, this.paddle.y - 4);
        this.lasers.push(leftLaser, rightLaser);
        this.audio.playLaserFire();
      }
    }

    // 3. Update Balls using 4x Sub-stepping for Glitch-Free Collisions
    const SUB_STEPS = 4;
    const subDt = dt / SUB_STEPS;

    for (let step = 0; step < SUB_STEPS; step++) {
      for (let i = this.balls.length - 1; i >= 0; i--) {
        const ball = this.balls[i];

        if (ball.isStuck) {
          if (step === 0) ball.updateStuck(this.paddle);
          continue;
        }

        const prevX = ball.x;
        const prevY = ball.y;

        // Move sub-step and check solid black side walls
        const hitBoundary = ball.stepMove(subDt, this.paddle);
        if (hitBoundary === 'WALL') {
          // Bounced on solid black unbreakable side wall
          this.audio.playSilverHit();
          this.particleSystem.spawn(ball.x, ball.y, 11, 4);
        }

        // Check Paddle collision if ball is moving downward
        if (ball.vy > 0) {
          const pBounds = this.paddle.getBounds();
          if (
            ball.x + ball.radius >= pBounds.left &&
            ball.x - ball.radius <= pBounds.right &&
            ball.y + ball.radius >= pBounds.top &&
            ball.y - ball.radius <= pBounds.top + 12
          ) {
            if (this.paddle.catchActive) {
              ball.attachToPaddle(this.paddle, ball.x - (this.paddle.x + this.paddle.width / 2));
              this.audio.playPaddleBounce();
            } else {
              const hitFactor = this.paddle.getHitOffset(ball.x);
              ball.bounceFromPaddle(this.paddle, hitFactor);
              this.audio.playPaddleBounce();
            }
            continue;
          }
        }

        // Check Ball vs Bricks collision with previous-position normal resolution
        this.checkBallBrickCollisions(ball, prevX, prevY);
      }
    }

    // Update ball trails and remove dead balls
    for (let i = this.balls.length - 1; i >= 0; i--) {
      const ball = this.balls[i];
      ball.recordTrail();
      if (ball.isDead()) {
        this.balls.splice(i, 1);
      }
    }

    // Check if all balls were lost
    if (this.balls.length === 0) {
      this.handleLifeLost();
      return;
    }

    // 4. Update Lasers
    for (let i = this.lasers.length - 1; i >= 0; i--) {
      const laser = this.lasers[i];
      laser.update(dt);

      for (const brick of this.bricks) {
        if (!brick.destroyed && brick.type !== BRICK_TYPES.EMPTY && laser.checkCollision(brick)) {
          laser.destroyed = true;
          this.damageBrick(brick, laser.x, laser.y);
          break;
        }
      }

      if (laser.destroyed) {
        this.lasers.splice(i, 1);
      }
    }

    // 5. Update Collectible Powerup Capsules
    for (let i = this.capsules.length - 1; i >= 0; i--) {
      const capsule = this.capsules[i];
      capsule.update(dt);

      if (capsule.checkCollision(this.paddle)) {
        this.applyPowerup(capsule.type);
        this.particleSystem.spawn(capsule.x, capsule.y, capsule.colorIndex, 12);
        capsule.destroyed = true;
      }

      if (capsule.destroyed) {
        this.capsules.splice(i, 1);
      }
    }

    // 6. Update Bricks & Particles
    this.bricks.forEach((b) => b.update(dt));
    this.particleSystem.update(dt);

    // 7. Check Level Clear Condition
    if (this.levelManager.getRemainingDestructibleCount() === 0 && this.state === GAME_STATES.PLAYING) {
      this.triggerLevelClear();
    }
  }

  checkBallBrickCollisions(ball, prevX, prevY) {
    const bRad = ball.radius;

    for (const brick of this.bricks) {
      if (brick.destroyed || brick.type === BRICK_TYPES.EMPTY) continue;

      const bounds = brick.getBounds();

      // Find closest point on brick AABB to ball center
      const closestX = Math.max(bounds.left, Math.min(ball.x, bounds.right));
      const closestY = Math.max(bounds.top, Math.min(ball.y, bounds.bottom));

      const dx = ball.x - closestX;
      const dy = ball.y - closestY;
      const distSq = dx * dx + dy * dy;

      if (distSq < bRad * bRad) {
        // Precise continuous directional entry detection:
        // By using prevX and prevY from before the sub-step movement,
        // we determine exactly which face of the brick the ball struck
        // and strictly clamp position outside the brick to prevent sticking.

        if (prevY + bRad <= bounds.top + 1.0) {
          // Struck from TOP: reflect upward
          ball.y = bounds.top - bRad - 0.1;
          ball.vy = -Math.abs(ball.vy);
        } else if (prevY - bRad >= bounds.bottom - 1.0) {
          // Struck from BOTTOM: reflect downward
          ball.y = bounds.bottom + bRad + 0.1;
          ball.vy = Math.abs(ball.vy);
        } else if (prevX + bRad <= bounds.left + 1.0) {
          // Struck from LEFT: reflect leftward
          ball.x = bounds.left - bRad - 0.1;
          ball.vx = -Math.abs(ball.vx);
        } else if (prevX - bRad >= bounds.right - 1.0) {
          // Struck from RIGHT: reflect rightward
          ball.x = bounds.right + bRad + 0.1;
          ball.vx = Math.abs(ball.vx);
        } else {
          // Corner penetration fallback: resolve based on penetration depths
          const overlapLeft = (ball.x + bRad) - bounds.left;
          const overlapRight = bounds.right - (ball.x - bRad);
          const overlapTop = (ball.y + bRad) - bounds.top;
          const overlapBottom = bounds.bottom - (ball.y - bRad);

          const minOverlapX = Math.min(overlapLeft, overlapRight);
          const minOverlapY = Math.min(overlapTop, overlapBottom);

          if (minOverlapX < minOverlapY) {
            if (overlapLeft < overlapRight) {
              ball.x = bounds.left - bRad - 0.1;
              ball.vx = -Math.abs(ball.vx);
            } else {
              ball.x = bounds.right + bRad + 0.1;
              ball.vx = Math.abs(ball.vx);
            }
          } else {
            if (overlapTop < overlapBottom) {
              ball.y = bounds.top - bRad - 0.1;
              ball.vy = -Math.abs(ball.vy);
            } else {
              ball.y = bounds.bottom + bRad + 0.1;
              ball.vy = Math.abs(ball.vy);
            }
          }
        }

        // Anti-stall: Prevent the ball from getting trapped horizontally
        if (Math.abs(ball.vy) < 1.2) {
          ball.vy = ball.vy < 0 ? -1.8 : 1.8;
        }

        this.damageBrick(brick, closestX, closestY);
        break; // Only 1 brick collision per ball per sub-step
      }
    }
  }

  damageBrick(brick, hitX, hitY) {
    const wasDestroyed = brick.hit();
    this.particleSystem.spawn(hitX, hitY, brick.colorIndex, wasDestroyed ? 14 : 6);

    if (brick.isGold || brick.type === BRICK_TYPES.BLACK_WALL) {
      this.audio.playSilverHit();
      return;
    }

    if (wasDestroyed) {
      this.audio.playBrickDestroy();
      this.addScore(brick.points);

      const capsule = this.levelManager.rollCollectible(brick, this.difficulty);
      if (capsule) {
        this.capsules.push(capsule);
        this.audio.playPowerupSpawn();
      }
    } else {
      if (brick.isSilver) {
        this.audio.playSilverHit();
      } else {
        this.audio.playBrickHit(brick.colorIndex);
      }
    }
  }

  applyPowerup(type) {
    this.audio.playPowerupCollect();
    this.addScore(100);

    switch (type) {
      case COLLECTIBLE_TYPES.SLOW:
        this.balls.forEach((b) => b.slowDown());
        break;

      case COLLECTIBLE_TYPES.CATCH:
      case COLLECTIBLE_TYPES.LASER:
      case COLLECTIBLE_TYPES.EXPAND:
        this.paddle.applyPowerup(type);
        break;

      case COLLECTIBLE_TYPES.DISRUPT:
        if (this.balls.length < 6) {
          const newBalls = [];
          this.balls.forEach((b) => {
            newBalls.push(b.clone(0.95));
            newBalls.push(b.clone(1.05));
          });
          this.balls.push(...newBalls);
        }
        break;

      case COLLECTIBLE_TYPES.BREAK:
        this.triggerLevelClear(true);
        break;

      case COLLECTIBLE_TYPES.PLAYER:
        this.lives++;
        this.audio.playExtraLife();
        this.ui.updateHUD(this.score, this.highScore, this.levelManager.getCurrentLevelNumber(), this.lives);
        break;

      default:
        break;
    }
  }

  handleLifeLost() {
    this.lives--;
    this.audio.playBallLost();
    this.renderer.triggerFlash(1.0);
    this.ui.updateHUD(this.score, this.highScore, this.levelManager.getCurrentLevelNumber(), this.lives);

    if (this.lives <= 0) {
      this.input.exitPointerLock();
      this.state = GAME_STATES.GAME_OVER;
      this.audio.playGameOver();
      this.ui.showState(this.state, {
        score: this.score,
        highScore: this.highScore,
      });
    } else {
      this.resetFieldForLife();
    }
  }

  triggerLevelClear(warp = false) {
    this.state = GAME_STATES.LEVEL_CLEAR;
    const bonus = warp ? 2000 : 1000;
    this.addScore(bonus);
    this.audio.playLevelClear();
    this.levelClearTimer = 2.4;
    this.ui.showState(this.state, { bonus });
  }

  advanceToNextLevel() {
    if (this.levelManager.hasNextLevel()) {
      this.bricks = this.levelManager.nextLevel(this.difficulty);
      this.resetFieldForLife();
      this.state = GAME_STATES.PLAYING;

      // Play unique background music for the new level
      this.audio.playLevelBGM(this.levelManager.getCurrentLevelNumber());

      this.input.requestPointerLock();
      this.ui.showState(this.state);
      this.ui.updateHUD(this.score, this.highScore, this.levelManager.getCurrentLevelNumber(), this.lives);
    } else {
      this.input.exitPointerLock();
      this.state = GAME_STATES.VICTORY;
      this.audio.playLevelClear();
      this.ui.showState(this.state, { score: this.score });
    }
  }

  addScore(points) {
    this.score += points;
    if (this.score > this.highScore) {
      this.highScore = this.score;
      localStorage.setItem('arkanoid_c64_high', this.highScore.toString());
    }
    this.ui.updateHUD(this.score, this.highScore, this.levelManager.getCurrentLevelNumber(), this.lives);
  }

  render(dt) {
    this.renderer.render({
      dt,
      time: this.gameTime,
      bricks: this.bricks,
      paddle: this.paddle,
      balls: this.balls,
      capsules: this.capsules,
      lasers: this.lasers,
      particleSystem: this.particleSystem,
      level: this.levelManager.getCurrentLevelNumber(),
    });
  }
}
